import { useEffect, useRef, useState, type ReactElement } from 'react'

import { AudioPlayer } from './AudioPlayer'
import { voiceEnglish, type VoiceText } from './voice-text'
const english = (key: VoiceText): string => voiceEnglish[key]

async function normalizeRecording(blob: Blob): Promise<File> {
  const context = new AudioContext()
  try {
    const decoded = await context.decodeAudioData(await blob.arrayBuffer())
    const samples = Math.min(Math.floor(decoded.duration * 16000), 120 * 16000)
    if (samples < 1600) throw new Error('Recording is too short')
    const offline = new OfflineAudioContext(1, samples, 16000)
    const source = offline.createBufferSource()
    source.buffer = decoded
    source.connect(offline.destination)
    source.start()
    const audio = (await offline.startRendering()).getChannelData(0)
    const bytes = new ArrayBuffer(44 + samples * 2)
    const view = new DataView(bytes)
    const tag = (offset: number, value: string) => {
      for (let i = 0; i < value.length; i++) view.setUint8(offset + i, value.charCodeAt(i))
    }
    tag(0, 'RIFF'); view.setUint32(4, bytes.byteLength - 8, true); tag(8, 'WAVEfmt ')
    view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true)
    view.setUint32(24, 16000, true); view.setUint32(28, 32000, true)
    view.setUint16(32, 2, true); view.setUint16(34, 16, true); tag(36, 'data'); view.setUint32(40, samples * 2, true)
    for (let i = 0; i < samples; i++) {
      const value = Math.max(-1, Math.min(1, audio[i]))
      view.setInt16(44 + i * 2, value < 0 ? value * 32768 : value * 32767, true)
    }
    return new File([bytes], 'voice-message.wav', { type: 'audio/wav' })
  } finally { await context.close() }
}

export function VoiceRecorder({ upload, disabled = false, text = english }: {
  upload: (file: File) => Promise<void>; disabled?: boolean; text?: (key: VoiceText) => string
}): ReactElement {
  const [phase, setPhase] = useState<'idle' | 'recording' | 'processing' | 'preview' | 'uploading'>('idle')
  const [file, setFile] = useState<File | null>(null)
  const [url, setUrl] = useState('')
  const [seconds, setSeconds] = useState(0)
  const [error, setError] = useState<VoiceText | null>(null)
  const recorder = useRef<MediaRecorder | null>(null)
  const stream = useRef<MediaStream | null>(null)
  const discard = useRef(false)
  const gone = useRef(false)
  const timer = useRef<ReturnType<typeof setInterval> | undefined>(undefined)
  useEffect(() => {
    gone.current = false
    const stopWhenHidden = () => {
      if (document.visibilityState === 'hidden' && recorder.current?.state === 'recording') recorder.current.stop()
    }
    document.addEventListener('visibilitychange', stopWhenHidden)
    return () => {
      document.removeEventListener('visibilitychange', stopWhenHidden)
      gone.current = true
      clearInterval(timer.current)
      if (recorder.current?.state === 'recording') recorder.current.stop()
      stream.current?.getTracks().forEach((track) => { track.stop() })
    }
  }, [])
  useEffect(() => () => { if (url) URL.revokeObjectURL(url) }, [url])

  const start = async () => {
    setError(null)
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined' || typeof AudioContext === 'undefined') {
      setError('unsupported'); return
    }
    for (const audio of document.querySelectorAll<HTMLAudioElement>('audio[data-support-audio]')) audio.pause()
    setPhase('processing')
    try {
      const input = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } })
      if (gone.current) { input.getTracks().forEach((track) => { track.stop() }); return }
      stream.current = input
      const next = new MediaRecorder(input)
      recorder.current = next
      discard.current = false
      const chunks: BlobPart[] = []
      let encodedBytes = 0
      next.ondataavailable = (event) => {
        encodedBytes += event.data.size
        if (encodedBytes > 5 * 1024 * 1024) {
          discard.current = true
          setError('failed')
          if (next.state === 'recording') next.stop()
          return
        }
        if (event.data.size) chunks.push(event.data)
      }
      next.onstop = () => {
        clearInterval(timer.current)
        input.getTracks().forEach((track) => { track.stop() })
        if (gone.current) return
        if (discard.current) { setPhase('idle'); return }
        setPhase('processing')
        void normalizeRecording(new Blob(chunks, { type: next.mimeType })).then((recorded) => {
          if (gone.current) return
          setFile(recorded); setUrl(URL.createObjectURL(recorded)); setPhase('preview')
        }).catch(() => { if (!gone.current) { setError('failed'); setPhase('idle') } })
      }
      next.onerror = () => {
        discard.current = true
        input.getTracks().forEach((track) => { track.stop() })
        clearInterval(timer.current)
        if (!gone.current) { setError('failed'); setPhase('idle') }
      }
      next.start(250)
      const started = performance.now()
      setSeconds(0); setPhase('recording')
      timer.current = setInterval(() => {
        const elapsed = Math.floor((performance.now() - started) / 1000)
        setSeconds(Math.min(120, elapsed))
        if (elapsed >= 120 && next.state === 'recording') next.stop()
      }, 200)
    } catch (reason) {
      stream.current?.getTracks().forEach((track) => { track.stop() })
      setError(reason instanceof DOMException && reason.name === 'NotAllowedError' ? 'denied' : 'failed')
      setPhase('idle')
    }
  }
  const remove = () => {
    discard.current = true
    if (recorder.current?.state === 'recording') recorder.current.stop()
    setFile(null); setUrl(''); setPhase('idle'); setError(null)
  }
  const send = async () => {
    if (!file || phase === 'uploading') return
    setPhase('uploading'); setError(null)
    try { await upload(file); if (!gone.current) remove() }
    catch { if (!gone.current) { setError('uploadFailed'); setPhase('preview') } }
  }
  return <div className="support-recorder" data-phase={phase}>
    {error && <p role="alert" className="mb-2 text-sm text-danger">{text(error)}</p>}
    {phase === 'idle' && <div className="flex flex-wrap items-center gap-3">
      <button type="button" className="btn btn-secondary btn-sm" title={text('limit')} disabled={disabled} onClick={() => { void start() }}><svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3M8 22h8" /></svg>{text('record')}</button>
      <span className="sr-only">{text('limit')}</span>
    </div>}
    {phase === 'recording' && <div>
      <div className="support-recording-top"><span role="status" className="support-record-status"><span aria-hidden="true" className="support-record-dot" />{text('recording')}</span>
        <span className="support-record-clock" dir="ltr">{Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')} <span className="text-xs text-ink-2">/ 2:00</span></span>
      </div>
      <progress className="support-record-progress" max={120} value={seconds} aria-label={text('limit')} />
      <div className="support-record-actions">
        <button type="button" className="btn btn-primary btn-sm" onClick={() => recorder.current?.stop()}><svg aria-hidden="true" width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><rect x="3" y="3" width="10" height="10" rx="2" /></svg>{text('stop')}</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={remove}>{text('discard')}</button>
      </div>
    </div>}
    {phase === 'processing' && <p role="status" className="text-sm text-ink-2">{text('processing')}</p>}
    {(phase === 'preview' || phase === 'uploading') && <div className="space-y-2">
      <p className="support-record-preview-title"><svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 10v4m5-8v12m4-15v18m4-15v12m5-8v4" /></svg>{text('preview')}</p>
      <AudioPlayer key={url} src={url} label={text('preview')} text={text} />
      {phase === 'uploading' ? <div role="status"><span className="text-sm">{text('uploading')}</span><progress className="w-full" aria-label={text('uploading')} /></div> : <div className="support-record-actions">
        <button type="button" className="btn btn-primary btn-sm" onClick={() => { void send() }}>{text('send')}</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={remove}>{text('discard')}</button>
      </div>}
    </div>}
  </div>
}
